/**
 * Download an item's file, saved under its real filename.
 *
 * Deliberately a bare `fetch` and NOT `apiFetch`: `/api/items/:id/file` 302s to a signed GCS URL,
 * and an `Authorization` header forces the browser to preflight that cross-origin leg — which the
 * bucket's GET/HEAD-only CORS config rejects, so the whole download fails. `credentials:
 * 'same-origin'` sends the `__session` cookie to our API (accepted by `server/middleware/auth.ts`)
 * and nothing to GCS.
 *
 * The endpoint serves `Content-Disposition: inline` by DEFAULT (it doubles as the preview source),
 * which is why the happy path re-anchors the blob client-side to get a save-as instead of a
 * navigation. `?download=1` asks it for `attachment` instead — see the endpoint for why that has to
 * be per-request.
 *
 * ── THE FALLBACK ONLY COVERS WHAT IT CAN ACTUALLY FIX (AlexAmin/marcus#429) ───────────────────────
 * It was added in c433c121 for one case: a DMS-sourced file, where the endpoint 302s to
 * Drive/SharePoint and a cross-origin blob read genuinely cannot work. It also catches the other
 * unreadable case, a signed GCS URL whose bucket CORS allowlist does not name this origin
 * (#307/#357 — measured: the bytes come back 200, but `access-control-allow-origin` is present only
 * for `http://localhost:3050`).
 *
 * Two things it must NOT do, both of which turned a failure into something that looked like success:
 *
 *   1. **Never navigate to the preview URL.** `inline` renders the file in a tab; the user reads
 *      that as a download and nothing lands on disk. The fallback goes to the `download=1` variant,
 *      so the response is an `attachment` and the browser saves it. For a DMS file the flag is
 *      inert — that branch redirects to Drive/SharePoint, which is the right outcome anyway.
 *   2. **Never swallow a refusal from our own API.** A non-OK same-origin response is not a
 *      cross-origin problem, so navigating to the same URL just reproduces it as a rendered error
 *      page. It stays an error and reaches the caller.
 *
 * `window.open` is not used: it runs after awaits, outside the click's user-gesture window, and the
 * popup blocker drops it silently. The repo's other two download paths anchor instead
 * (`useArtifactDownload`, `useConfirmCards.downloadDocument`); this matches them.
 */
import { saveBlob } from '~/utils/saveBlob'

/**
 * Adds `download=1` without disturbing a query the caller already built (`?generation=…`).
 *
 * Exported for `useConfirmCards.downloadDocument`, the chat's own copy of the blob-then-navigate
 * shape (AlexAmin/marcus#498). That copy keeps its own bare `fetch` for a real reason — see its
 * comment — so the helper above is not a drop-in for it, but "which URL does the fallback
 * navigate to" is the one piece that must not be decided twice: it was, and the second decision
 * was the bug.
 */
export function asDownloadUrl(url: string): string {
  const parsed = new URL(url, window.location.origin)
  parsed.searchParams.set('download', '1')
  return parsed.toString()
}

/**
 * Hands the URL to the browser as a navigation. A new tab because a DMS file lands on
 * Drive/SharePoint and a same-tab navigation would take the user out of Marcus; an `attachment`
 * response closes the tab itself once the download starts. No `download` attribute — the browser
 * ignores it on a cross-origin resource, which is the whole of #363.
 */
function navigateToDownload(href: string): void {
  const a = document.createElement('a')
  a.href = href
  a.target = '_blank'
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

export async function downloadItemFile(url: string, fileName?: string | null): Promise<void> {
  let res: Response
  try {
    res = await fetch(url, { credentials: 'same-origin' })
  } catch (err) {
    console.warn('[download] blob read unavailable for this origin, navigating to the download URL:', err)
    navigateToDownload(asDownloadUrl(url))
    return
  }

  if (!res.ok) {
    // Navigating here would render the same refusal as a page, which is the error-read-as-a-value
    // this helper used to produce. Let the caller see it.
    throw new Error(`[download] ${url} failed: HTTP ${res.status}`)
  }

  saveBlob(await res.blob(), fileName || 'document')
}
